<template>
  <div class="order-grid col-sm-12 col-md-12 col-lg-12">
    <div v-if="!isMobile()" class="medications-header">
      <h1 class="medications-title">Medications</h1>
      <div class="medications-nav">
        <router-link to="/currentMedications" class="nav-link">Current</router-link>
        <span class="nav-separator">|</span>
        <span class="nav-link active">Inactive</span>
        <span class="nav-separator">|</span>
        <router-link to="/medAdministration" class="nav-link">Administration</router-link>
        <span class="nav-separator">|</span>
        <router-link to="/prePourMedication" class="nav-link">Prepour</router-link>
      </div>
    </div>
    <template v-else>
      <div class="header mobile">MEDICATIONS</div>
      <div class="nav-links mobile">
        <router-link to="/currentMedications" class="mobile">Current</router-link>
        <span id="current-link" class="mobile active">Inactive</span>
        <router-link to="/medAdministration">Administration</router-link>
        <router-link to="/prePourMedication">Prepour</router-link>
      </div>
    </template>
    
    <template v-if="!isMobile()">
      <!-- Inactive Medications Table -->
      <div v-if="inactiveMeds && inactiveMeds.length > 0" class="container">
        <table>
          <thead>
            <tr>
              <th style="width: 20%;">Medication</th>
              <th>Quantity</th>
              <th>Diagnosis</th>
              <th>Frequency</th>
              <th>Start to End</th>
              <th>Route</th>
              <th>Status</th>
              <th>Writer</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="med in inactiveMeds" :key="'med-inactive' + med.medentryid">
              <td data-label="Medication" style="width: 20%;">
                <a 
                  href="#" 
                  class="med-link" 
                  @click.prevent="showMedInfo(med)"
                >{{ med.medname }}</a>
              </td>
              <td data-label="Amount">{{ med.med_amount }}</td>
              <td data-label="Diagnosis">{{ med.diagnose_code }}</td>
              <td data-label="Frequency">{{ med.med_frequency }}</td>
              <td data-label="Start to End">
                {{
                  `${med.med_startdate ? med.med_startdate : "—"} to ${
                    formatEndDate(med.med_enddate)
                  }`
                }}
              </td>
              <td data-label="Route">{{ med.route }}</td>
              <td data-label="Status">{{ med.status || med.medchangetype || 'Inactive' }}</td>
              <td data-label="Writer">{{ med.writer }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template v-else>
      <!-- Mobile view -->
      <div v-if="inactiveMeds && inactiveMeds.length > 0" class="table-container">
        <div class="accordion">
          <div
            v-for="med in inactiveMeds"
            :key="'med-inactive' + med.medentryid"
            :id="'med-inactive' + med.medentryid"
            @click="showMed('inactive' + med.medentryid)"
            class="accordion-item"
          >
            <button class="accordion-button">
              <span class="med-link-mobile">{{ med.shorthand ? med.shorthand : med.medname }}</span>
            </button>
            <div class="accordion-content">
              <p><b>Medication: </b>{{ `${med.medname}` }}</p>
              <p><b>Amount: </b>{{ `${med.med_amount}` }}</p>
              <p><b>Frequency: </b>{{ `${med.med_frequency}` }}</p>
              <p><b>Diagnosis: </b>{{ `${med.diagnose_code}` }}</p>
              <p><b>Route: </b>{{ `${med.route}` }}</p>
              <p><b>Status: </b>
                <span :class="getStatusClass(med.status || med.medchangetype || 'Inactive')">
                  {{ med.status || med.medchangetype || 'Inactive' }}
                </span>
              </p>
              <p>{{ `${med.med_startdate} to ${formatEndDate(med.med_enddate)}` }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { CURR_API } from "@/constants/apis";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "InactiveMedications",
  data() {
    return {
      allMeds: null,
      showMedInfoModal: false,
      selectedMed: null,
    };
  },
  async mounted() {
    this.loadMedications();
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    patient: function () {
      return this.allData.patient;
    },
    inactiveMeds: function () {
      if (!this.allMeds) return null;
      return this.getLatestMedsByStatus(true);
    },
  },
  methods: {
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    async loadMedications() {
      let content = {
        MedGrid: {
          API_Meth: "PopulateMedGrid",
          pid: this.allData.patient.externId,
        },
      };
      await axios
        .post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        })
        .then((res) => {
          console.log(res);
          if (res.data.gridhtml) {
            this.allMeds = res.data.data;
          } else {
            console.log("Error loading meds for " + this.patient.externId);
            this.allMeds = null;
          }
        });
    },
    getLatestMedsByStatus(isDiscontinued) {
      if (!this.allMeds || !Array.isArray(this.allMeds)) return [];
      
      const medGroups = {};
      this.allMeds.forEach(med => {
        const medName = (med.medname || '').trim();
        if (!medName) return;
        
        if (!medGroups[medName]) {
          medGroups[medName] = [];
        }
        medGroups[medName].push(med);
      });
      
      const resultMeds = [];
      
      Object.keys(medGroups).forEach(medName => {
        const group = medGroups[medName];
        
        group.sort((a, b) => {
          const dateA = a.orderdate || '';
          const dateB = b.orderdate || '';
          const timeA = a.ordertime || '';
          const timeB = b.ordertime || '';
          
          if (dateA !== dateB) {
            return dateB.localeCompare(dateA);
          }
          return timeB.localeCompare(timeA);
        });
        
        const latestMed = group[0];
        const status = (latestMed.status || latestMed.medchangetype || '').toLowerCase();
        const isLatestDiscontinued = status === 'discontinue' || status === 'discontinued';
        const isLatestActive = status === 'active' || status === 'active-pending';
        
        if (isDiscontinued) {
          if (isLatestDiscontinued) {
            resultMeds.push(latestMed);
          } else if (isLatestActive) {
            const previousOrders = group.slice(1);
            resultMeds.push(...previousOrders);
          }
        } else {
          if (isLatestActive) {
            resultMeds.push(latestMed);
          }
        }
      });
      
      resultMeds.sort((a, b) => {
        const dateA = a.orderdate || '';
        const dateB = b.orderdate || '';
        const timeA = a.ordertime || '';
        const timeB = b.ordertime || '';
        
        if (dateA !== dateB) {
          return dateB.localeCompare(dateA);
        }
        return timeB.localeCompare(timeA);
      });
      
      return resultMeds;
    },
    showMed(id) {
      let entry = document.getElementById(`med-${id}`);
      if (entry) {
        entry.classList.toggle("active");
      }
    },
    getStatusClass(status) {
      if (!status) return 'status-inactive';
      const statusLower = status.toLowerCase();
      if (statusLower === 'discontinue' || statusLower === 'discontinued') {
        return 'status-discontinued';
      } else if (statusLower === 'active' || statusLower === 'active-pending') {
        return 'status-active';
      } else {
        return 'status-inactive';
      }
    },
    showMedInfo(med) {
      // Don't show modal on mobile - accordion handles the display
      if (this.isMobile()) {
        return;
      }
      this.selectedMed = med;
      this.showMedInfoModal = true;
    },
    closeMedInfo() {
      this.showMedInfoModal = false;
      this.selectedMed = null;
    },
    formatEndDate(endDate) {
      if (!endDate || endDate === '1971-01-01' || endDate === '1970-01-01') {
        return '—';
      }
      return endDate;
    },
  },
};
</script>

<style scoped lang="scss">
.header {
  text-align: center;
  background-color: #003366;
  color: white;
  padding: 10px 0;
  font-size: 1.5em;
  margin-top: 5%;
}

.medications-header {
  margin-top: 20px;
  margin-bottom: 20px;
}

.medications-title {
  color: #000;
  text-align: center;
  margin: 0 0 15px 0;
  font-size: 2.5em;
  font-weight: normal;
}

.medications-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.nav-link {
  color: #0060ff;
  text-decoration: none;
  font-size: 2em;
  padding: 5px 10px;
  transition: color 0.2s;
}

.nav-link:hover {
  color: #0040cc;
  text-decoration: underline;
}

.nav-link.active {
  color: #000;
  font-weight: bold;
  text-decoration: none;
  pointer-events: none;
}

.nav-link.disabled {
  color: #666;
  cursor: not-allowed;
  text-decoration: none;
  pointer-events: none;
}

.nav-separator {
  color: #666;
  margin: 0 5px;
}

.container {
  margin: 20px auto;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #4caf50;
  color: white;
}

tr:nth-child(even) {
  background-color: #d5d5d5;
}

.nav-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #0b4987;
  padding: 10px 0;
}

.nav-links a,
.nav-links span {
  color: white;
  text-decoration: none;
  padding: 10px 20px;
  background-color: #0b4987;
  border-radius: 5px;
  margin: 5px 0;
  width: 80%;
  text-align: center;
}

.nav-links a.active,
#current-link {
  background-color: #003366;
  pointer-events: none;
}

.nav-links span.disabled {
  background-color: #555;
  cursor: not-allowed;
  pointer-events: none;
}

.accordion {
  margin-top: 10px;
}

.accordion-item {
  border-bottom: 1px solid #ddd;
}

.accordion-button,
button.accordion-button {
  background-color: #052c65 !important; /* Always use this color */
  color: #ffffff !important; /* White text for readability */
  padding: 15px;
  width: 100%;
  text-align: left;
  border: none !important;
  outline: none;
  cursor: pointer;
  font-size: 18px;
  transition: background-color 0.3s ease;
  opacity: 1 !important; /* Ensure no transparency */
}

.accordion-button:hover,
button.accordion-button:hover {
  background-color: #003366 !important; /* Dark blue on hover */
  color: #ffffff !important;
  opacity: 1 !important;
}

.accordion-item.active .accordion-button,
.accordion-item.active button.accordion-button {
  background-color: #052c65 !important; /* Keep same color when active */
  color: #ffffff !important;
  opacity: 1 !important;
}

.accordion-content {
  display: none;
  background-color: #f4f4f4;
  padding: 15px;
}

.accordion-item.active .accordion-content {
  display: block;
}

.table-container {
  margin-top: 10px;
}

/* Medication link styles */
.med-link {
  color: #0060ff;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
}

.med-link:hover {
  color: #0040cc;
  text-decoration: none;
}

.med-link:active {
  color: #002080;
}

.med-link-mobile {
  color: #ffffff;
  text-decoration: none;
  font-weight: 500;
  display: block;
}

/* Status color coding */
.status-active {
  color: #28a745;
  font-weight: 600;
}

.status-discontinued {
  color: #dc3545;
  font-weight: 600;
}

.status-inactive {
  color: #6c757d;
  font-weight: 600;
}
</style>
